/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useTranslation} from "react-i18next";
import {PackageItem} from "types/entities/PackageItem";
import clsx from "clsx";
import React from "react";
import {ServerImage} from "components/ServerImage";
import {currencyNumberFormat, getSelectedUpsell, translate} from "utils/utility";
import {useTypedSelector} from "helpers/reducers";
import ButtonSubmit from "pages/Front/Booking/components/ButtonSubmit";
import PackageUpsellItemPriceWithStock
    from "pages/Front/Booking/components/PackageUpsell/components/PackageUpsellItemPriceWithStock";

interface PackageProps {
    setSelectedPackageUpsell: () => void;
    className: string;
    packageItem: PackageItem;
    inputs: React.ReactNode | React.ReactNode[];
    showRemoveButton: boolean;
    contained?: PackageItem | boolean;
    callToAction?: boolean;
    style: any;
    children?: React.ReactNode | React.ReactNode[];
    isGroupedProduct?: boolean;
    isLoading?: boolean;
    isNewLayout?: boolean;
    isSeller?: boolean;
    quantity?: number;
}

export const PackageUpsellItemContainer = (props: PackageProps) => {
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const selectedUpsell = getSelectedUpsell(bookingProcess.upsell, props.packageItem);
    const {t} = useTranslation();

    return (
        <div
            className={clsx("packageUpsellItemContainer overflow-hidden w-full flex flex-col bg-white rounded-xl shadow", props.className)}
            style={props.style}
        >
            <div className={"w-full flex flex-col md:flex-row md:items-stretch"}>
                <div className={"flex flex-row w-full md:w-auto md:self-stretch md:flex-1"}>
                    <div
                        className={clsx(
                            "grow flex flex-col md:items-stretch w-full max-w-full",
                            props.isSeller ? "sm:w-[128px] sm:max-w-[128px]" : "md:w-[16rem] md:max-w-[16rem] h-full"
                        )}
                    >
                        <div
                            className={clsx(
                                "itemImage flex h-full overflow-hidden w-full grow rounded-t-lg md:rounded-tr-none",
                                props.isSeller ? "sm:max-w-[128px] aspect-auto" : "md:max-h-full aspect-[64/56] sm:max-h-[250px] md:max-w-[16rem]"
                            )}
                        >
                            {props.packageItem?.image && props.packageItem.image !== "" && (
                                <ServerImage
                                    alt={"Package Item Image"}
                                    className={"w-full md:max-h-auto object-cover object-center"}
                                    src={props.packageItem.image}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div
                    className={clsx(
                        "flex flex-col items-start gap-x-4 w-full",
                        props.isSeller ? "justify-between pl-2 pr-2 py-2 gap-y-1" : "justify-center px-4 lg:px-8 py-4 gap-y-2 md:gap-y-0"
                    )}
                    data-package-item-id={props.packageItem.id}
                >
                    <div className={"flex flex-row justify-center items-start gap-x-4 w-full"}>
                        <h3 className="text-black leading-tight text-base sm:text-xl lg:text-xl xl:text-2xl text-left font-bold flex-grow w-full">
                            {translate(props.packageItem.name)}
                        </h3>

                        <div className={"flex-1 text-center md:hidden"}>
                            {props.isGroupedProduct ? (
                                <div className={"font-light text-gray-800 text-xs sm:text-sm"}>{t("Included in Package")}</div>
                            ) : (
                                <PackageUpsellItemPriceWithStock packageItem={props.packageItem} quantity={props?.quantity} />
                            )}
                        </div>
                    </div>

                    {props?.isSeller && (
                        <div>
                            {props.packageItem?.description_html ? (
                                <p className={"w-full"} dangerouslySetInnerHTML={{__html: translate(props.packageItem?.description_html)}} />
                            ) : (
                                <p className={"w-full"}>{translate(props.packageItem?.description)}</p>
                            )}
                        </div>
                    )}

                    {props?.isSeller ? (
                        <div className={"flex items-center self-end border p-2 rounded-md bg-gray-50 w-auto max-w-[160px]"}>
                            {props.isGroupedProduct ? (
                                <div className={"font-light text-gray-800 text-xs sm:text-sm"}>{t("Included in Package")}</div>
                            ) : (
                                <PackageUpsellItemPriceWithStock isSeller={true} packageItem={props.packageItem} quantity={props?.quantity} />
                            )}
                        </div>
                    ) : (
                        <div className={"flex justify-center items-center h-full w-full text-black text-xs sm:text-sm lg:text-base font-medium"}>
                            {props.packageItem?.description_html ? (
                                <p className={"w-full"} dangerouslySetInnerHTML={{__html: translate(props.packageItem?.description_html)}} />
                            ) : (
                                <p className={"w-full"}>{translate(props.packageItem?.description)}</p>
                            )}
                        </div>
                    )}
                </div>

                <div className={"md:border-l border-gray-300 flex flex-col items-center justify-center space-y-2 px-4 pt-0 pb-4 md:p-2"}>
                    <div className={clsx(props?.isSeller ? "hidden" : "hidden md:flex")}>
                        {props.isGroupedProduct ? (
                            <div className={"font-light text-gray-800 text-xs sm:text-sm"}>{t("Included in Package")}</div>
                        ) : (
                            <PackageUpsellItemPriceWithStock className={"items-center"} packageItem={props.packageItem} quantity={props?.quantity} />
                        )}
                    </div>

                    <div className="items-center w-full justify-center flex flex-col md:flex-row md:space-x-4">
                        <div className={"w-full md:w-[180px] flex flex-row md:flex-col justify-between md:justify-center items-center"}>
                            {props.inputs}
                            {!props.isGroupedProduct && (
                                <div className={"md:mt-4"}>
                                    <ButtonSubmit
                                        buttonType={props.showRemoveButton ? "selectedBlack" : "arbo-orange"}
                                        callToAction={props.callToAction}
                                        className={clsx(
                                            "!w-auto md:grow rounded-lg !px-2 !py-1.5 md:!py-1 xl:!py-1.5 xl:!px-2 !text-lg xl:!text-xl !font-bold"
                                        )}
                                        disabled={props.contained && !props.showRemoveButton}
                                        icon={props.showRemoveButton ? "check" : "plusChar"}
                                        id={"ButtonSelectUpsell"}
                                        label={props.showRemoveButton ? t("Selected") : t("Add")}
                                        onClick={props.setSelectedPackageUpsell}
                                    />
                                    {selectedUpsell && (
                                        <div className={"mt-1 flex flex-col text-center sr-only"}>
                                            <span className={"text-sm text-gray-600"}>{t("Total price")}:</span>
                                            <span className={"font-bold leading-none"}>
                                                {currencyNumberFormat(selectedUpsell.count * props.packageItem.price)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {props.children}
            </div>
        </div>
    );
};
